import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { database, ref, get } from '../firebase';
import { FiPhone, FiLock, FiCheckSquare, FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

// Initialize EmailJS with your Public Key (use environment variable in production)
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'UU7m2yJcG2hGOO-sl');

const Login = () => {
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingPassword, setIsSendingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Allow only digits and limit to 10
      const cleanedValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: cleanedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const mobile = formData.mobile.trim();
    if (!mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]+$/.test(mobile)) {
      newErrors.mobile = 'Mobile number must contain only digits';
    } else if (mobile.length !== 10) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const userRef = ref(database, `videoplayer/Users/${formData.mobile}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();
        if (userData.mobile === formData.mobile && userData.password === formData.password) {
          if (userData.role === 'admin') {
            const adminUser = {
              fullName: userData.adminName || userData.fullName || 'Admin User',
              mobile: userData.mobile,
              username: userData.username || 'admin',
              phoneNumber: userData.mobile,
              bio: userData.bio || 'Administrator',
              termsAccepted: true,
              profilePictureUrl: userData.profilePictureUrl || null,
              role: 'admin',
            };
            localStorage.setItem('currentUser', JSON.stringify(adminUser));
            toast.success('Admin Login Successful!');
            setFormData({ mobile: '', password: '' });
            setErrors({});
            setIsSubmitting(false);
            navigate('/admin/dashboard');
            return;
          }
          const status = userData.status || 'pending';
          if (status !== 'accepted') {
            setErrors({ submit: `Your account is ${status}. Please wait for admin approval or contact support.` });
            setIsSubmitting(false);
            return;
          }
          const userToStore = {
            fullName: userData.fullName || null,
            mobile: userData.mobile,
            username: userData.username || null,
            phoneNumber: userData.phoneNumber || formData.mobile,
            bio: userData.bio || null,
            termsAccepted: userData.termsAccepted || false,
            profilePictureUrl: userData.profilePictureUrl || null,
            role: userData.role || 'user', // Include role for stockist
          };
          localStorage.setItem('currentUser', JSON.stringify(userToStore));
          toast.success('Login Successful!');
          setFormData({ mobile: '', password: '' });
          setErrors({});
          if (userData.role === 'stockist') {
            navigate('/stockist/dashboard');
          } else {
            navigate('/home');
          }
        } else {
          setErrors({ submit: 'Invalid mobile number or password.' });
        }
      } else {
        setErrors({ submit: 'No account found with this mobile number.' });
      }
    } catch (error) {
      setErrors({ submit: 'An error occurred during login. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setIsSendingPassword(true);

    try {
      const usersRef = ref(database, 'videoplayer/Users');
      const snapshot = await get(usersRef);

      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const userWithEmail = Object.entries(usersData).find(
          ([id, user]) => user.email === forgotEmail
        );

        if (!userWithEmail) {
          toast.error('Email address not found in our records.');
          setIsSendingPassword(false);
          return;
        }

        const userData = userWithEmail[1];
        const userPassword = userData.password;

        const templateParams = {
          email: forgotEmail,
          password: userPassword,
          full_name: userData.fullName || 'User',
          mobile: userData.mobile || 'N/A',
        };

        await emailjs.send(
          'service_xipk8ox',
          'template_7es3ivv',
          templateParams
        );

        toast.success('Password sent to your email address!');
        setShowForgotPassword(false);
        setForgotEmail('');
      } else {
        toast.error('Email address not found in our records.');
      }
    } catch (error) {
      toast.error('Failed to send email. Please contact support.');
    } finally {
      setIsSendingPassword(false);
    }
  };

  const resetForgotPasswordForm = () => {
    setForgotEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6 text-center flex items-center justify-center">
          <FiPhone className="mr-3 text-blue-600 text-3xl" /> Log In
        </h2>
        {Object.keys(errors).length > 0 && (
          <div
            className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm border border-red-200"
            aria-live="polite"
          >
            {Object.values(errors).map((error, idx) => (
              <p key={idx}>{error}</p>
            ))}
          </div>
        )}
        
        {!showForgotPassword ? (
          <>
            <div className="space-y-5">
              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FiPhone className="mr-2 text-blue-600" /> Mobile Number <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="mobile"
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile number"
                  pattern="[0-9]{10}"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  aria-invalid={!!errors.mobile}
                  aria-describedby={errors.mobile ? "mobile-error" : undefined}
                />
                <p className="text-gray-500 text-xs mt-1">Enter a 10-digit mobile number without country code or spaces.</p>
                {errors.mobile && <p id="mobile-error" className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FiLock className="mr-2 text-blue-600" /> Password <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <FiEyeOff className="w-5 h-5" />
                    ) : (
                      <FiEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && <p id="password-error" className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 flex items-center justify-center disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging In...
                  </span>
                ) : (
                  <>
                    <FiCheckSquare className="mr-2" /> Log In
                  </>
                )}
              </button>
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              Don't have an account?{' '}
              <a href="/" className="text-blue-600 hover:underline">Sign Up</a>
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              Forgot password?{' '}
              <button 
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-600 hover:underline focus:outline-none"
              >
                Reset it here
              </button>
            </p>
          </>
        ) : (
          <div className="mt-1 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FiMail className="mr-2 text-blue-600" /> Reset Password
            </h3>
            {!isSendingPassword ? (
              <>
                <div className="mb-4">
                  <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="forgotEmail"
                    type="email"
                    placeholder="Enter registered email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="button"
                  onClick={sendPassword}
                  disabled={isSendingPassword || !forgotEmail}
                  className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  Send Password to Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    resetForgotPasswordForm();
                  }}
                  className="w-full mt-2 bg-gray-200 text-gray-800 p-3 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-300"
                >
                  Back to Login
                </button>
              </>
            ) : (
              <div className="text-center">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Sending password to your email...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;